import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ArrayHelpers, Copy, DomHelpers, FloatingMenu, Loading, SystemText, DebugInfo } from '@salesperformanceint/e4shared';
import Switch from 'react-switch';
import queryString from 'query-string';
import CompetencyList from 'components/general/competencyList';
import PageHeader from 'components/general/pageHeader';
import NoResults from 'components/general/noResults';
import SortFilter from 'components/general/sortFilter';
import UserMenu from 'components/user/menu';
import TaskIcon from '../task/components/taskIcon';
import { apiGet, apiPatch } from 'services';
import { hasModule } from 'helpers/profile';
import { addDays, isPast, isToday, e4Format, e4Formats } from 'helpers/dates';

import './pathways.css';
import Enroll from './components/enroll';
import { ratingColour } from 'helpers/metrics';
import { getPermissions } from 'helpers/permissions';
import { translateCompetency } from 'helpers/translations';
import { setPrismMenu } from 'actions/ui';

export const UserPathways = ({ match, history }) => {
    const dispatch = useDispatch();

    const [error, setError] = React.useState(false);
    const [profile, setProfile] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [noResults, setNoResults] = React.useState(true);
    const [loadingProfile, setLoadingProfile] = React.useState(true);
    const [assignmentId, setAssignmentId] = React.useState(0);
    const [assignment, setAssignment] = React.useState({});
    const [assignments, setAssignments] = React.useState([]);
    const [allAssignments, setAllAssignments] = React.useState([]);
    const [pathways, setPathways] = React.useState([]);
    const [availablePathways, setAvailablePathways] = React.useState([]);
    const [enrollablePathways, setEnrollablePathways] = React.useState([]);
    const [heights, setHeights] = React.useState({});
    const [showEnroll, setShowEnroll] = React.useState(false);
    const [showCompleted, setShowCompleted] = React.useState(false);
    const [hasCompleted, setHasCompleted] = React.useState(false);
    const [permissions, setPermissions] = React.useState(getPermissions());
    const [loaded, setLoaded] = React.useState(false);

    const id = match.params.id;
    const { coaching, downline, user } = useSelector(state => state.user);
    const leftAsideOpen = useSelector(state => state.ui.leftAsideOpen);
    const isMe = profile?.id === user.id;

    if (!hasModule(user, 'pathways')) history.replace('/');
    if (user && !match.params.id) {
        history.replace({
            pathname: match.path.replace(':id?', user.id),
            search: location.search,
        });
    }

    React.useEffect(() => {
        if (profile?.id && user?.id) {
            const { prism_UI } = user.companySettings;
            if (prism_UI) {
                history.replace('/pathways');
            }

            setLoaded(true);

            setPermissions(getPermissions(profile, user, coaching, downline));

            if (profile.id === user.id) {
                dispatch(setPrismMenu('Pathways'));
            } else {
                dispatch(setPrismMenu('MyTeam'));
            }
        }
    }, [profile?.id, user?.id]);

    React.useEffect(() => {
        DebugInfo('component', 'UserPathways');
    }, []);

    const resizeHandler = () => {
        setTimeout(() => {
            DomHelpers.arrangeTiles('career');
            DomHelpers.arrangeTiles('career-tasks');
        }, 250);
    };

    React.useEffect(() => {
        window.addEventListener('resize', resizeHandler);
        resizeHandler();

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []); // empty array => run only once

    React.useEffect(() => resizeHandler, [leftAsideOpen]);

    React.useEffect(() => {
        api.loadPathways();
        api.loadProfile();
    }, []);

    React.useEffect(() => {
        const temp = [];
        pathways.forEach(p => {
            p.assignments.forEach(a => {
                temp.push({
                    ...p,
                    ...a,
                    name: p.name,
                    tasks: p.tasks,
                    competencies: p.competencies.map(c => translateCompetency(user.language_Code, c)),
                });
            });
        });

        setAllAssignments(temp);
        setAssignments(temp.filter(a => showCompleted || !a.date_Completed));
        setHasCompleted(temp.some(a => a.date_Completed));
    }, [pathways, showCompleted]);

    React.useEffect(() => {
        const { id } = queryString.parse(location.search);
        const idVal = id ? parseInt(id) : 0;

        if (idVal && allAssignments.find(a => a.id === idVal && a.date_Completed)) {
            setShowCompleted(true);
        }

        if (assignments && Array.isArray(assignments) && assignments.length > 0) {
            setAssignmentId(idVal || assignments[0].id);
        } else if (allAssignments && Array.isArray(allAssignments) && allAssignments.length > 0) {
            setShowCompleted(true);
            setAssignmentId(allAssignments[0].id);
        }
    }, [assignments]);

    React.useEffect(() => {
        if (assignmentId) {
            const ass = allAssignments.find(a => a.id === assignmentId);
            if (ass) {
                const setAssessmentValues = (curr, old) => {
                    const ass = { ...curr, ...old };

                    const cjson = JSON.parse(ass.competency_Values);
                    ass.competencies = ass.competencies.map(c => ({ ...c, minRating: cjson[c.id] }));

                    if (
                        !ArrayHelpers.existsAndHasLength(ass.competencies) &&
                        ArrayHelpers.existsAndHasLength(ass.competency_Ids)
                    ) {
                        ass.competencies = ass.competency_Ids.map(id => {
                            const c = curr.competencies.find(c => c.id === id);
                            return { ...c, minRating: cjson[id] };
                        });
                    }

                    return ass;
                };

                setHeights({});

                if (ass.complete_Json) {
                    const jsonPath = JSON.parse(ass.complete_Json);
                    setAssignment(setAssessmentValues(ass, jsonPath));
                } else {
                    setAssignment(setAssessmentValues(ass));
                }
            }
        }
    }, [assignmentId]);

    const text = {
        title: SystemText('Pathways/Title'),
        positiveIndicators: SystemText('Pathways/PositiveIndicators'),
        enrolled: SystemText('Pathways/Enrolled'),
        header: {
            completed: x => SystemText('Pathways/Header/Completed', x),
            late: x => SystemText('Pathways/Header/Late', x),
            overdue: x => SystemText('Pathways/Header/Overdue', x),
            due: x => SystemText('Pathways/Header/Due', x),
        },
        status: {
            notAchieved: SystemText('Pathways/Status/NotAchieved'),
            late: SystemText('Pathways/Status/Late'),
            awaiting: SystemText('Pathways/Status/Awaiting'),
            locked: SystemText('Pathways/Status/Locked'),
            outstanding: SystemText('Pathways/Status/Outstanding'),
        },
        minRating: SystemText('Pathways/MinRating'),
        currentRating: SystemText('Pathways/CurrentRating'),
        completed: x => SystemText('Pathways/Completed', x),
        buttons: {
            enrol: SystemText('Pathways/Buttons/Enrol'),
            refresh: SystemText('Pathways/Buttons/Refresh'),
            toggle: SystemText('Pathways/Buttons/Toggle'),
        },
        noResults: {
            title: SystemText('Pathways/NoResults/Title'),
            you: SystemText('Pathways/NoResults/You'),
            them: SystemText('Pathways/NoResults/Them', profile?.first_Name),
        },
        incomplete: SystemText('Incomplete'),
    };

    const api = {
        loadProfile: () => {
            setLoadingProfile(true);
            id &&
                apiGet(
                    `profile/${id}`,
                    user.guid,
                    response => {
                        setProfile(response);
                        setLoadingProfile(false);
                    },
                    () => setError(true)
                );
        },
        loadPathways: () => {
            setLoading(true);
            id &&
                apiGet(
                    `pathway/${id}`,
                    user.guid,
                    response => {
                        setPathways(response);
                        setNoResults(response.filter(x => ArrayHelpers.existsAndHasLength(x?.assignments)).length === 0);
                        setLoading(false);
                        api.loadAvailablePathways(response);
                    },
                    () => setError(true)
                );
        },
        loadAvailablePathways: pathways => {
            apiGet('pathway-list', user.guid, response => {
                if (response?.length) {
                    const available = response.map(a => {
                        const current = (pathways || []).find(p => p.id === a.id);
                        const assignment = current?.assignments.find(ca => !ca.date_Completed);
                        const disabled = assignment;
                        const extra = assignment ? `${text.enrolled} ${e4Format(assignment.date_Assigned)}` : '';
                        return {
                            ...a,
                            disabled,
                            extra,
                        };
                    });

                    setAvailablePathways(available);
                }
            });
        },
        checkCompleted: () => {
            apiPatch(`pathways-check/${id}`, null, user.guid, () => api.loadPathways());
        },
    };

    const [due, setDue] = React.useState(new Date());

    React.useEffect(() => {
        if (assignment?.date_Assigned) {
            setDue(new Date(assignment.date_Due));
        }
    }, [assignment]);

    React.useEffect(() => {
        if (user?.administrator) {
            setEnrollablePathways(availablePathways);
        } else {
            setEnrollablePathways(availablePathways.filter(p => p.manager));
        }
    }, [availablePathways, user?.administrator]);

    const [today, setToday] = React.useState(false);

    React.useEffect(() => {
        setToday(isToday(due));
    }, [due]);

    const [completed, setCompleted] = React.useState(null);

    React.useEffect(() => {
        setCompleted(assignment?.date_Completed ? new Date(assignment.date_Completed) : null);
    }, [assignment]);

    const [late, setLate] = React.useState(null);

    React.useEffect(() => {
        setLate(
            assignment ? (!completed && isPast(due) && !today) || (completed && completed.setHours(0, 0, 0, 0) > due) : false
        );
    }, [assignment, due]);

    const header = () => {
        if (!assignment) return '';
        return completed && completed <= due
            ? text.header.completed(e4Format(completed, e4Formats.long))
            : completed && new Date(completed) > due
              ? text.header.late(e4Format(completed, e4Formats.long))
              : isPast(due) && !today
                ? text.header.overdue(e4Format(due, e4Formats.long))
                : text.header.due(e4Format(due, e4Formats.long));
    };

    const bullet = task => {
        const late = task.date_Completed && new Date(task.date_Completed).setHours(0, 0, 0, 0) > due;

        return (
            <div key={`bullt-${task.id}`} className={`flex ${task.locked ? 'text-disabled' : ''}`}>
                <div className="career hidden-xs">
                    <div className={`icon icon${task.icons?.length}`}>
                        <TaskIcon icons={task.icons?.length ? task.icons : ['calendar-check']} extraClass="super" />
                    </div>
                </div>
                <div className={`flex-grow career ${task.locked ? 'locked' : ''}`}>
                    <div className="description flex" style={{ height: '100%' }}>
                        <div className="flex-grow">
                            {permissions.pathways.canViewObjectives ? (
                                <Link to={`/objectives/${profile.id}/objective/${task.id}`}>
                                    <label className="pointer">{task.name}</label>
                                </Link>
                            ) : (
                                <label>{task.name}</label>
                            )}
                            {task.description && (
                                <div className="mb10">
                                    <Copy value={task.description} />
                                </div>
                            )}
                            {task.competency_Ids?.length > 0 && (
                                <div className="hidden-xs">
                                    <CompetencyList competencies={task.competencies} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="career">
                    <div className="icon">
                        {task.fully_Completed && task.metric_Task && !task.goal_Met ? (
                            <div>
                                <i className="fa fa-times text-danger" />
                                <div className="text-sm mt10 mb10">{e4Format(task.date_Completed)}</div>
                                <div className="label label-danger">{text.status.notAchieved}</div>
                            </div>
                        ) : task.fully_Completed ? (
                            <div>
                                <i className="fa fa-check text-success super" />
                                <div className="text-sm mt10 mb10">{e4Format(task.date_Completed)}</div>
                                {late && <div className="label label-danger">{text.status.late}</div>}
                            </div>
                        ) : task.waiting_Approval ? (
                            <div>
                                <i className="fa fa-user text-warning super" />
                                <div className="text-sm mt10">{text.status.awaiting}</div>
                            </div>
                        ) : task.locked ? (
                            <div>
                                <i className="fa fa-lock text-danger" />
                                <div className="text-sm mt10">{text.status.locked}</div>
                            </div>
                        ) : (
                            <span className="text-disabled">
                                <i className="fa fa-warning text-danger" />
                                <div className="text-sm">{text.status.outstanding}</div>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const rating = rating => (
        <div className={`min-rating card-${ratingColour(rating)}`}>
            <label>{rating}</label>
            <div>{text.minRating}</div>
        </div>
    );

    const expandText = (e, c) => {
        if (e.target.parentElement.classList.contains('lines')) {
            e.target.parentElement.classList.remove('lines');
            e.target.parentElement.classList.remove('lines3');
            const kids = e.target.closest('.competency-row').children;
            for (let x = 0; x < kids.length; x++) {
                const temp = heights;
                temp[c.id] = kids[x].style.height;
                setHeights(temp);
                kids[x].style.height = '';
            }
        } else {
            e.target.parentElement.classList.add('lines');
            e.target.parentElement.classList.add('lines3');
            const kids = e.target.closest('.competency-row').children;
            for (let x = 0; x < kids.length; x++) {
                kids[x].style.height = heights[c.id];
            }
        }
    };

    const competency = c => (
        <div className="flex competency-row" key={`competency-${c.id}`}>
            <div className="career hidden-xs">
                <div className="icon">
                    <i className="fa fa-star-sharp super" />
                </div>
            </div>
            <div className={`flex-grow career ${c.locked ? 'locked' : ''}`}>
                <div className="description flex">
                    <div className="flex-grow">
                        <label>
                            {c.area} - {c.name}
                        </label>
                        <div className="lines lines-3 pointer" onClick={e => expandText(e, c)}>
                            <Copy value={c.description} />
                            {ArrayHelpers.existsAndHasLength(c.positiveIndicators) && (
                                <div className="mt10">
                                    <div className="bold">{text.positiveIndicators}</div>
                                    {c.positiveIndicators.map(pi => (
                                        <div key={`positive-indicator-${pi.id}`}>
                                            <i className="fa fa-check-square text-success mr10" />
                                            {pi.statement}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div>{rating(c.minRating)}</div>
                </div>
            </div>
            <div className="career">
                <div className="icon">
                    <div>
                        {c.rating < c.minRating ? (
                            <i className="fa fa-warning text-danger" />
                        ) : (
                            <i className="fa fa-check text-success super" />
                        )}
                        <div className="text-sm mt10">
                            {text.currentRating}: {c.rating}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const selectOthers = () => {
        if (!assignments || !assignment?.id) return null;
        return (
            <div className="title-floatingmenu float-right">
                <FloatingMenu
                    buttonText={`${assignment.name}${
                        assignment.date_Completed ? ` - ${text.completed(e4Format(assignment.date_Completed))}` : ''
                    }`}
                    align="right"
                >
                    {assignments.map((a, i) => (
                        <li key={`path-${a.id}-${i}`} onClick={() => setAssignmentId(a.id)}>
                            <i className="fa fa-person-hiking mr10 super" />
                            {a.name}
                            {a.date_Completed && <b> - {text.completed(e4Format(a.date_Completed))}</b>}
                        </li>
                    ))}
                </FloatingMenu>
            </div>
        );
    };

    const extra = () => (
        <div className="flex">
            {availablePathways?.length > 0 && permissions.pathways.canEnrol && enrollablePathways?.length > 0 && (
                <button className="btn btn-success" onClick={() => setShowEnroll(true)}>
                    <i className="fa fa-plus mr10" />
                    {text.buttons.enrol}
                </button>
            )}
            <UserMenu profile={profile} user={user} hide={'pathways'} />
        </div>
    );

    const toggle = () => (
        <div>
            {!hasCompleted ? null : (
                <label className={`flex flex-center hidden-xs ${showCompleted ? 'switch-on' : 'switch-off'}`}>
                    <span className="mr5">{text.buttons.toggle}</span>
                    <Switch className="react-switch" onChange={setShowCompleted} checked={showCompleted} />
                </label>
            )}
        </div>
    );

    if (!loaded) return <Loading />;

    return (
        <div id="my-career" data-testid="my-career">
            <PageHeader
                title={text.title}
                overrideProfile={profile ? profile : null}
                extra={extra}
                after={loading ? null : selectOthers}
            />
            <section className="content mtm10" data-testid="pathway-content">
                <SortFilter toggle={toggle} filterFields={null} />
                {loading || loadingProfile || !permissions.pathways.canView ? (
                    <Loading />
                ) : noResults ? (
                    <div>
                        <NoResults title={text.noResults.title} message={isMe ? text.noResults.you : text.noResults.them} />
                    </div>
                ) : (
                    <div className="careers">
                        {assignment.description && (
                            <div className="mb20 mtm10">
                                <Copy value={assignment.description} />
                            </div>
                        )}
                        <div className={`career-header flex flex-center mb10 ${late ? 'late' : ''}`}>
                            <div className="flex-grow">{header()}</div>
                            <div>
                                <i className="fa fa-chevron-right" />
                                <i className="fa fa-chevron-right" />
                                <i className="fa fa-chevron-right" />
                            </div>
                        </div>
                        {assignment?.competencies && assignment.competencies.map(c => competency(c))}
                        {assignment?.tasks &&
                            assignment.tasks.filter(t => t.pathway_Assignment_Id === assignmentId).map(t => bullet(t))}
                        {!completed && (
                            <div className="mt10">
                                {text.incomplete}
                                <button className="btn btn-primary ml10 btn-sm" onClick={() => api.checkCompleted()}>
                                    {text.buttons.refresh}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </section>
            <Enroll
                close={() => {
                    setShowEnroll(false);
                }}
                reload={() => api.loadPathways()}
                show={showEnroll}
                pathways={enrollablePathways}
                profile={profile}
            />
        </div>
    );
};

export default UserPathways;
